import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {AddEmployeeService} from 'src/app/services/add-employee.service';
import {CloseHandlerService} from 'src/app/services/close-handler.service';
import {
  columnTypes,
  ITableOnClickCell,
  UITableHeader,
} from 'src/app/shared/ui-kits/table/table.models';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {Router} from '@angular/router';
import {IDirtyPhoneNumbers} from 'src/app/interfaces/personal-data.interface';
import {
  IDropdownPm,
  INewEmployeeInterview,
  IPositionNewEmployee,
} from 'src/app/interfaces/dropdown-new-employee.interface';
import {IMessengers} from 'src/app/interfaces/messengers.interface';
import {Utils} from 'src/app/shared/utils/utils';
import {
  combineLatestWith,
  debounceTime,
  distinctUntilChanged,
  filter,
  startWith,
  switchMap,
} from 'rxjs';
import {
  noWhitespaceValidator,
  wordValidation,
} from 'src/app/directives/word-validators.directive';
import {
  emailValidationPattern,
  onlyLettersRegExp,
  regExpEnglishAndSpace,
} from '../../../validators/letterValidators';
import {
  uniqueEmailValidator,
  wordCountValidator,
} from 'src/app/validators/custom.validators';
import {
  IEmployeeUniqueness,
  IGeneralRecruiter,
  ISalaryRecruiter,
} from 'src/app/interfaces/add-employee.interface';
import {CompanyManagementService} from 'src/app/services/company-management.service';
import {EmployeeStatuses} from 'src/app/interfaces/roles.interace';
import {DateTime} from 'luxon';
import {EmploymentForm} from 'src/app/interfaces/employee.interface';

/**
 * The component contains a form for filling basic data when adding an employee
 */
@UntilDestroy()
@Component({
  selector: 'app-general-recruiter',
  templateUrl: './general-recruiter.component.html',
  styleUrls: ['./general-recruiter.component.scss'],
})
export class GeneralRecruiterComponent implements OnInit, OnDestroy {
  public form!: UntypedFormGroup; // form for adding an employee
  public isEditable = true;
  public statusItems: IDropdownObject[] = []; // possible items for status
  public departmentItems: IDropdownObject[] = []; // possible items for department
  public subDepartmentItems: IDropdownObject[] = []; // possible items for sub-department
  public employmentFormItems: IDropdownObject[] = []; // possible items for employmentForm
  public positionItems: IPositionNewEmployee[] = []; // possible items for position for selected department
  public subPositionItems: IPositionNewEmployee[] = []; // possible items for position for selected sub-department
  public positionFullItems: IPositionNewEmployee[] = []; // all possible items for position
  public levelItems: IDropdownObject[] = []; // possible items for level for selected position
  public longLevelItems: IDropdownObject[] = []; // possible items for level
  public pmItems: IDropdownPm[] = []; // possible items for pm
  public unitItems: IDropdownObject[] = []; // possible items for unit
  public hrItems: IDropdownObject[] = []; // possible items for hr
  public peItems: IDropdownObject[] = []; // possible items for pe
  public osItems: IDropdownObject[] = []; // possible items for os
  public countryItems: IDropdownObject[] = []; // possible items for city
  public techStackItems: IDropdownObject[] = []; // possible items for tech stack
  public lineManagerItems: IDropdownObject[] = []; // possible items for Line Manager
  public onlyLettersRegExp = onlyLettersRegExp; // regExp to enter only letters
  public headersInterview: UITableHeader[] = [
    // headers for the table with interviews
    {key: 'selected', index: 0, type: columnTypes.checkBox},
    {key: 'interviewer', index: 1},
    {key: 'startDate', index: 2, type: columnTypes.datePicker},
    {key: 'status', index: 3},
    {key: 'comment', index: 4},
  ];
  public changed = false; // indicates whether the form has changed
  public formValueDefault!: IGeneralRecruiter; // saved value of the form
  public isPositionSelected = false; // shows that position is selected
  public firstLoad = true;
  public isTechStack = false;
  public techStackPositions = ['Software Engineer', 'Competence Lead'];
  public isFieldInvalid = Utils.isFieldInvalid;
  public validateAllFormFields = Utils.validateAllFormFields;
  public isContractor = false;

  @ViewChild('saveButton') saveButton!: ElementRef; // button save
  public phoneNumbersComponentData: IMessengers = {
    // default value for messengers component
    phoneNumber: '',
    isTelegram: false,
    isViber: false,
    isWhatsApp: false,
  };
  public mask = [
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    ' ',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    ' ',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    ' ',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
  ];
  public isEqualObjects = Utils.isEqualObjects;
  public regExpEnglish = /^[a-zA-Z\s]+$/;
  public regExpEnglishNameSurname = /^[a-zA-Z-\s]+$/;
  public regExpEnglishAndSpace = regExpEnglishAndSpace;
  public isExistEmployee = false;
  public status: string = 'Current';

  public readonly DELIVERY_DEPARTMENT = 'Delivery';
  public readonly MANAGEMENT_SUBDEPARTMENT = 'Management';
  private readonly NO_HR = {id: 0, name: 'No HR'};

  get isDepartmentDelivery(): boolean {
    return (
      this.form.get('department')?.value?.name === this.DELIVERY_DEPARTMENT
    );
  }

  get isSubDepartmentManagement(): boolean {
    return (
      this.subDepartmentControl?.value?.name === this.MANAGEMENT_SUBDEPARTMENT
    );
  }

  get isDepartmentSelected(): boolean {
    return this.departmentControl?.value?.name;
  }

  get isSubDepartment(): boolean {
    return this.subDepartmentControl?.value?.name;
  }

  get subDepartmentControl(): AbstractControl {
    return this.form.controls['subDepartment'];
  }

  get departmentControl(): AbstractControl {
    return this.form.controls['department'];
  }

  get nameControl(): AbstractControl {
    return this.form.get('name')!;
  }

  get surnameControl(): AbstractControl {
    return this.form.get('surname')!;
  }

  get startDateControl(): AbstractControl {
    return this.form.get('startDate')!;
  }

  constructor(
    private addEmployeeService: AddEmployeeService,
    private closeHandler: CloseHandlerService,
    private router: Router,
    private detector: ChangeDetectorRef,
    private companyManagementService: CompanyManagementService
  ) {}

  ngOnInit(): void {
    this.form = this.buildGeneralRecruiterForm();
    this.initDropdowns();

    this.closeHandler.clickSavePopup
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.saveButton.nativeElement.click();
      });

    this.formValueDefault = this.form.value;
    this.closeHandler.notSaveForm.pipe(untilDestroyed(this)).subscribe(() => {
      this.changed = true;
      this.form.patchValue(this.formValueDefault);
      this.phoneNumbersComponentData = {
        ...this.phoneNumbersComponentData,
      };
      this.detector.detectChanges();
      this.changed = false;
    });

    this.subscribeToDepartmentChanges();
    this.subscribeToPositionChanges();
    this.subscribeToSubDepartmentChanges();
    this.subscribeToFormChanges();
    this.subscribeToNameChanges();
    this.subscribeToEmploymentFormChanges();
    this.subscribeToPMChanges();
    this.subscribeToStartDateChanges();
  }

  buildGeneralRecruiterForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      name: new UntypedFormControl('', [
        Validators.required,
        wordValidation(this.regExpEnglishNameSurname),
        noWhitespaceValidator,
        Validators.maxLength(30),
      ]),
      surname: new UntypedFormControl('', [
        Validators.required,
        wordValidation(this.regExpEnglishNameSurname),
        noWhitespaceValidator,
        Validators.maxLength(30),
      ]),
      department: new UntypedFormControl(null, [Validators.required]),
      subDepartment: new UntypedFormControl(null),
      position: new UntypedFormControl(null, [Validators.required]),
      level: new UntypedFormControl(null),
      pm: new UntypedFormControl({value: null, disabled: true}),
      lineManager: new UntypedFormControl({value: null, disabled: true}),
      hr: new UntypedFormControl(null, [Validators.required]),
      pe: new UntypedFormControl(null, [Validators.required]),
      os: new UntypedFormControl(null, [Validators.required]),
      startDate: new UntypedFormControl(null, [Validators.required]),
      country: new UntypedFormControl(null, [Validators.required]),
      city: new UntypedFormControl(null, [
        Validators.required,
        Validators.maxLength(20),
      ]),
      commentFromRecruiter: new UntypedFormControl('', [
        Validators.maxLength(1000),
      ]),
      employmentForm: new UntypedFormControl(
        {name: EmploymentForm.Employee, id: 2},
        [Validators.required]
      ),
      phoneNumbers: new UntypedFormControl([]),
      file: new UntypedFormControl(null),
      interviews: new UntypedFormControl([]),
      anukoLogin: new UntypedFormControl(null, [
        Validators.maxLength(20),
        Validators.pattern(this.onlyLettersRegExp),
      ]),
      firstProject: new UntypedFormControl(null, [Validators.maxLength(30)]),
      contractorWorkingHoursPerWeek: new UntypedFormControl(null),
      unit: new UntypedFormControl(null, [Validators.required]),
      techStack: new UntypedFormControl(null),
      personalEmail: new UntypedFormControl(null, [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(emailValidationPattern),
      ]),
    });
  }

  subscribeToDepartmentChanges() {
    this.departmentControl.valueChanges
      .pipe(
        untilDestroyed(this),
        filter(data => data)
      )
      .subscribe(value => {
        Utils.disableControl('pm', this.form);
        this.departmentControl?.markAsTouched();

        if (value.name === this.DELIVERY_DEPARTMENT) {
          Utils.enableControl('subDepartment', this.form);
          this.subDepartmentControl.setValidators([Validators.required]);
        } else {
          Utils.disableControl('subDepartment', this.form);
          Utils.disableControl('pm', this.form);
          Utils.enableControl('lineManager', this.form);
          this.form.controls['lineManager'].setValidators([
            Validators.required,
          ]);
        }

        const filteredPosition = this.positionFullItems.filter(
          item => item.department.id === value.id
        );
        if (this.isDepartmentDelivery) {
          this.subPositionItems = filteredPosition;
        } else {
          this.positionItems = filteredPosition;
        }
        this.form.controls['position'].setValue(null);
        this.subDepartmentControl.setValue(null);

        this.detector.detectChanges();
      });
  }

  subscribeToPositionChanges() {
    (this.form.get('position') as AbstractControl).valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        if (value) {
          this.form.controls['level'].setValue(null);
          this.isPositionSelected = true;
        } else {
          this.isPositionSelected = false;
        }
        if (this.techStackPositions.includes(value?.name)) {
          Utils.enableControl('techStack', this.form);
          this.form.controls['techStack'].setValidators([Validators.required]);
          this.isTechStack = true;
        } else {
          Utils.disableControl('techStack', this.form);
          this.isTechStack = false;
        }
        this.levelItems = this.longLevelItems;
        this.detector.detectChanges();
      });
  }

  subscribeToSubDepartmentChanges() {
    this.subDepartmentControl?.valueChanges
      .pipe(
        untilDestroyed(this),
        filter(data => data)
      )
      .subscribe(value => {
        if (this.isSubDepartmentManagement) {
          Utils.disableControl('pm', this.form);
          Utils.enableControl('lineManager', this.form);
          this.form.controls['lineManager'].setValidators([
            Validators.required,
          ]);
        } else {
          Utils.enableControl('pm', this.form);
          this.form.controls['pm'].setValidators([Validators.required]);
          Utils.disableControl('lineManager', this.form);
        }

        if (this.isEditable) {
          this.form.controls['position'].setValue(null);
        }

        this.subPositionItems = this.positionFullItems.filter(
          item => item.department.name === value.name
        );
      });
  }

  subscribeToPMChanges() {
    this.form
      .get('pm')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(value => {
        if (value) {
          const unit = this.pmItems.find(v => v.id === value.id)?.unit;
          this.form.get('unit')?.setValue(unit);
        }
      });
  }

  subscribeToFormChanges() {
    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value: IGeneralRecruiter) => {
        if (
          value &&
          !this.changed &&
          !this.isEqualObjects(value, this.formValueDefault)
        ) {
          this.closeHandler.notSaved = true;
        }
      });
  }

  subscribeToNameChanges() {
    this.nameControl.valueChanges
      .pipe(
        debounceTime(1500),
        distinctUntilChanged(),
        combineLatestWith(
          this.surnameControl.valueChanges.pipe(
            debounceTime(1500),
            distinctUntilChanged()
          )
        )
      )
      .pipe(
        untilDestroyed(this),
        filter(v => {
          const IsBothValues = !!v[0].trim() && !!v[1].trim();
          if (!IsBothValues) {
            this.isExistEmployee = false;
          }
          return IsBothValues;
        }),
        switchMap(([firstName, lastName]) => {
          const employeeFullName: IEmployeeUniqueness = {
            firstName,
            lastName,
          };

          return this.addEmployeeService.isEmployeeUnique(employeeFullName);
        })
      )
      .subscribe((isNew: boolean) => {
        this.firstLoad = false;
        this.isExistEmployee = !isNew;
      });
  }

  subscribeToEmploymentFormChanges() {
    this.form
      .get('employmentForm')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(value => {
        const control = this.form.controls['contractorWorkingHoursPerWeek'];
        const hrControl = this.form.controls['hr'];
        if (!value?.name.includes(EmploymentForm.Employee)) {
          hrControl.setValue(null);
          hrControl.clearValidators();
          hrControl.updateValueAndValidity();
          control.setValidators([
            Validators.required,
            Validators.min(0),
            Validators.max(40),
          ]);
          control.updateValueAndValidity();
          this.isContractor = true;
        } else {
          hrControl.setValidators([Validators.required]);
          hrControl.updateValueAndValidity();
          control.setValue(null);
          control.clearValidators();
          control.updateValueAndValidity();
          this.isContractor = false;
        }
        if (this.formValueDefault.employmentForm?.name !== value?.name)
          this.resetSalaryValue();
      });
  }

  subscribeToStartDateChanges() {
    this.startDateControl.valueChanges
      .pipe(
        untilDestroyed(this),
        filter(data => data),
        distinctUntilChanged()
      )
      .subscribe((date: string) => {
        const isPending = Utils.formatStringToDateTime(date) > DateTime.now();
        this.status = isPending
          ? EmployeeStatuses.Pending
          : EmployeeStatuses.Current;
      });
  }

  resetSalaryValue() {
    this.addEmployeeService.salaryData = {} as ISalaryRecruiter;
    this.addEmployeeService.isSalaryData = false;
  }

  /**
   * save method sends the form to the server if the data on the "salary" tab is filled in or redirects to the "salary" tab
   */
  save(): void {
    if (this.form.invalid || this.isExistEmployee) {
      this.form.controls['pm'].markAsTouched();
      this.validateAllFormFields(this.form);
      return;
    }
    this.closeHandler.notSaved = false;
    this.formValueDefault = this.form.value;
    this.phoneNumbersComponentData = {
      ...this.form.controls['phoneNumbers'].value[0],
    };
    this.form
      .get('cardNumber')
      ?.setValue(+this.form.get('cardNumber')?.value.replace(/ /g, ''));

    const formValue = this.form.getRawValue();

    this.addEmployeeService.collectData(formValue);
    this.addEmployeeService.isGeneralData = true;

    if (this.addEmployeeService.isSalaryData) {
      this.addEmployeeService
        .addEmployee()
        .pipe(untilDestroyed(this))
        .subscribe(data => {
          this.addEmployeeService.cleanVariables();
          this.router.navigate(['/profile/', data]);
        });
    } else {
      this.router.navigate(['profile/adding/salary']);
    }
  }

  /**
   * Method, that set value for interviews.
   * Once user click on checkbox, it write the id.
   * @param event outputed data from the table components
   */
  interviewCheckBoxClick(event: ITableOnClickCell) {
    const selectedIds: number[] = event.data
      .filter((item: INewEmployeeInterview) => item.selected)
      .map((data: INewEmployeeInterview) => data.id);
    this.form.controls['interviews'].setValue(selectedIds);
  }

  /**
   * Method that set value for phoneNumbers control
   * @param event data from messengers compoent
   */
  setPhoneNumber(event: IDirtyPhoneNumbers) {
    this.form.controls['phoneNumbers'].setValue([event.value]);
  }

  getPhoneNumber() {
    if (this.addEmployeeService.generalData.phoneNumbers) {
      this.phoneNumbersComponentData =
        this.addEmployeeService.generalData.phoneNumbers[0];
    }
  }

  private initDropdowns() {
    this.addEmployeeService
      .getDropdowns()
      .pipe(untilDestroyed(this))
      .subscribe(data => {
        this.statusItems = data.statusType;
        this.departmentItems = data.department;
        this.subDepartmentItems = data.subDepartment;
        this.employmentFormItems = data.employmentForm;
        this.positionFullItems = data.position;
        this.longLevelItems = data.qualificationLevelLong;
        this.pmItems = data.pm;
        this.hrItems = data.hr.concat([this.NO_HR]);
        this.peItems = data.yesNo;
        this.osItems = data.os;
        this.countryItems = data.country;
        this.techStackItems = data.techStacks;
        this.lineManagerItems = data.lineManager;
        this.form.patchValue(this.addEmployeeService.generalData);
        this.getPhoneNumber();
        this.unitItems = this.companyManagementService.companyUnits;
        this.closeHandler.notSaved = false;
      });
  }

  /**
   * If the clicked route does not salary adding tab, clear the ultimate data, to avoid passing it for the next time
   */
  ngOnDestroy(): void {
    if (this.router.routerState.snapshot.url !== '/profile/adding/salary')
      this.addEmployeeService.cleanVariables();
  }
}
